/**
 * Error classifier for Smart Gate passport validation.
 *
 * Categorizes backend errors into two groups:
 * - DOCUMENT_INFO: Document/business logic issues → Redirect to counter immediately
 * - TECHNICAL_ISSUE: OCR/technical failures → Retry once, then redirect to counter
 */

export const ERROR_CATEGORIES = Object.freeze({
  TECHNICAL_ISSUE: "TECHNICAL_ISSUE",
  DOCUMENT_INFO: "DOCUMENT_INFO",
});

// Reason: GDRFA fault codes that indicate a document/business logic problem.
// These should never be retried — the user is shown Not Eligible directly.
const DOCUMENT_INFO_FAULT_CODES = new Set([
  "800014", // Not Allowed to Query Profiles
  "800007", // Invalid Search Param Combination
  "20014", // The person profile found is inactive
  "20013", // Profile Does Not Exist
]);

// Reason: GDRFA fault codes that indicate a transient/technical problem.
// A single retry may resolve the issue.
const TECHNICAL_FAULT_CODES = new Set([
  "800009", // Exception in processing the request
  "101503", // Error connecting to the back end
  "900901", // Invalid credentials supplied
  "800015", // NULL Or Empty values not allowed
  "500", // Exception while calling ms-profile-search
  "20015", // Invalid request passed to the service
  "800008", // Mismatching Case (Param valid)
]);

/**
 * Extracts a GDRFA fault code from the error message.
 * The backend concatenates the fault code and description: "{code} {description}".
 *
 * @param {string} message - The error message from the backend
 * @returns {string|null} The extracted fault code, or null if not found
 */
const extractFaultCode = (message) => {
  if (!message || typeof message !== "string") return null;

  // The message format from the backend is: "{faultCode} {description}"
  const firstToken = message.trim().split(/\s+/)[0];

  // Check if the first token looks like a numeric fault code
  if (/^\d+$/.test(firstToken)) {
    return firstToken;
  }

  return null;
};

/**
 * Determines if the error is an OCR/MRZ extraction failure.
 * The backend returns "Cannot extract mrz data: ..." for all Azure OCR failures.
 *
 * @param {string} message - The error message from the backend
 * @returns {boolean}
 */
const isOcrError = (message) => {
  if (!message || typeof message !== "string") return false;
  return message.toLowerCase().includes("cannot extract mrz data");
};

/**
 * Classifies a validation error into a category for determining the UI flow.
 *
 * @param {Object} errorResult - The error result object from passport validation
 * @param {string} errorResult.message - Error message from the backend
 * @param {number} [errorResult.statusCode] - HTTP status code from the backend
 * @returns {{ category: string, faultCode: string|null }}
 */
export const classifyError = (errorResult) => {
  if (!errorResult) {
    return { category: ERROR_CATEGORIES.TECHNICAL_ISSUE, faultCode: null };
  }

  const { message, statusCode } = errorResult;

  // OCR / MRZ extraction failures are always technical issues (retry may help with a better scan)
  if (isOcrError(message)) {
    return { category: ERROR_CATEGORIES.TECHNICAL_ISSUE, faultCode: null };
  }

  // Try to extract a GDRFA fault code from the message
  const faultCode = extractFaultCode(message);

  if (faultCode) {
    if (DOCUMENT_INFO_FAULT_CODES.has(faultCode)) {
      return { category: ERROR_CATEGORIES.DOCUMENT_INFO, faultCode };
    }

    if (TECHNICAL_FAULT_CODES.has(faultCode)) {
      return { category: ERROR_CATEGORIES.TECHNICAL_ISSUE, faultCode };
    }
  }

  // Fallback: classify by HTTP status code
  // 5xx and 401 errors are likely transient/technical
  if (statusCode >= 500 || statusCode === 401) {
    return { category: ERROR_CATEGORIES.TECHNICAL_ISSUE, faultCode };
  }

  // Unrecognized 4xx errors default to technical issue (retry once to be safe)
  return { category: ERROR_CATEGORIES.TECHNICAL_ISSUE, faultCode };
};
